//Helpers
import { SystemStyles } from '@Shelly/shelly-design-system';
import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
import { convertDashToUnderscore } from 'src/helpers/utilities';
//Types
import type { SystemStylesTypes } from '@Shelly/shelly-design-system';

export type ChartGradientColorType = {
	start: string;
	end: string;
	solid: string;
	labelIndicatorGradientColor?: boolean;
	tooltipIndicatorColor?: string;
	barGradientColor?: boolean;
	gradientOpacity?: { start: number; end: number };
};

export const getGradientColor = (style: string, chartType: string): ChartGradientColorType => {
	switch (style) {
		case 'consumption':
		case 'dynamicChart_current':
		case 'overall_consumption':
		case 'grid_source':
		case 'monthly_report_consumption':
		case 'max_distance':
			if (chartType == 'bar') {
				return {
					start: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_START),
					end: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_END),
					solid: getSystemStyle(SystemStyles.COLOR_CHART_CONSUMED_ENERGY),
					tooltipIndicatorColor: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY),
					labelIndicatorGradientColor: true,
					barGradientColor: true,
				};
			}
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_CONSUMED_ENERGY),
				tooltipIndicatorColor:
					chartType.startsWith('area') &&
					getSystemStyle(SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY),
				labelIndicatorGradientColor: chartType.startsWith('area'),
				gradientOpacity: { start: 0.4, end: 0.4 },
			};
		case 'avg_distance':
		case 'generated_energy':
		case 'dynamicChart_excluded':
			if (chartType == 'bar') {
				return {
					start: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_START),
					end: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
					solid: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
					barGradientColor: true,
				};
			}
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_START),
				end: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
			};
		case 'total_house_energy':
		case 'prev_period_reversed':
		case 'freq_measurement_avg':
		case 'tariff_intervals_utc':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PREV_GENERATED_ENERGY_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PREV_GENERATED_ENERGY_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PREV_GENERATED_ENERGY),
			};
		case 'prev_period_consumption':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_EXCLUDED_ENERGY_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_EXCLUDED_ENERGY),
			};
		case 'reversed':
		case 'returned':
		case 'overall_reversed':
		case 'monthly_report_reversed':
			if (chartType == 'bar') {
				return {
					start: getSystemStyle(SystemStyles.GRADIENT_CHART_GENERATED_ENERGY_START),
					end: getSystemStyle(SystemStyles.COLOR_CHART_GENERATED_ENERGY),
					solid: getSystemStyle(SystemStyles.COLOR_CHART_GENERATED_ENERGY),
					barGradientColor: true,
				};
			}
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_GENERATED_ENERGY_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_GENERATED_ENERGY_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_GENERATED_ENERGY),
			};
		case 'min_temperature':
		case 'tempSchedule_cool':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_TEMPERATURE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_TEMPERATURE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MIN_TEMPERATURE),
			};
		case 'max_temperature':
		case 'tempSchedule_heat':
		case 'negative_energy':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_TEMPERATURE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_TEMPERATURE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MAX_TEMPERATURE),
			};
		case 'humidity':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_HUMIDITY),
			};
		case 'phase_a':
		case 'min_distance':
		case 'phase_a_min_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_A),
			};
		case 'phase_b':
		case 'phase_b_min_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_B),
			};
		case 'phase_c':
		case 'phase_c_min_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_C),
			};
		case 'total_consumption':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_TOTAL),
			};
		case 'phase_a_returned':
		case 'phase_a_max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_A_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_A_RET),
			};
		case 'phase_b_returned':
		case 'phase_b_max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_B_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_B_RET),
			};
		case 'phase_c_returned':
		case 'phase_c_max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_C_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_C_RET),
			};
		case 'total_returned':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_RET_END),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PHASE_TOTAL_RET_START),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PHASE_TOTAL_RET),
			};
		case 'min_voltage':
		case 'voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_VOLTAGE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_VOLTAGE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MIN_VOLTAGE),
			};
		case 'max_voltage':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_VOLTAGE_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_VOLTAGE_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MAX_VOLTAGE),
			};
		case 'min_input':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_INPUT_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MIN_INPUT_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MIN_INPUT),
			};
		case 'max_input':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_INPUT_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_MAX_INPUT_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_MAX_INPUT),
			};
		case 'valve_position':
		case 'freq_measurement_min':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_TRV_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_TRV_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_TRV),
			};
		case 'tariff_intervals':
			return {
				start: getSystemStyle(SystemStyles.CHART_MUTED_START),
				end: getSystemStyle(SystemStyles.CHART_MUTED_END),
				solid: getSystemStyle(SystemStyles.COLOR_MUTED),
			};
		case 'ppm':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PPM),
			};
		case 'counter_accumulation':
		case 'freq_measurement_max':
			return {
				start: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_START),
				end: getSystemStyle(SystemStyles.GRADIENT_CHART_PPM_END),
				solid: getSystemStyle(SystemStyles.COLOR_CHART_PPM),
			};
	}
	console.log("missing chart's style: " + style);
	return {
		start: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_START),
		end: getSystemStyle(SystemStyles.GRADIENT_CHART_HUMIDITY_END),
		solid: getSystemStyle(SystemStyles.COLOR_CHART_HUMIDITY),
	};
};

export const getIntervalAvatarColors = (
	colorKey,
): {
	start: string;
	end: string;
	solid: string;
} => {
	const colorValues = Object.values(AvatarColorVariant).slice(0, -13);
	const currentColor = colorValues.find((color) => color === colorKey);
	if (!currentColor) return colorKey;
	const colorValue = convertDashToUnderscore(currentColor?.split('color-')[1].toUpperCase());

	if (colorValue) {
		return {
			start: getSystemStyle(SystemStyles[`GRADIENT_${colorValue}_CHART_START`]),
			end: getSystemStyle(SystemStyles[`GRADIENT_${colorValue}_CHART_END`]),
			solid: getSystemStyle(
				SystemStyles[`${convertDashToUnderscore(currentColor).toUpperCase()}`],
			),
		};
	}
	return colorKey;
};

/**
 * Turns an old-fashioned css hex color value into a rgb color value.
 *
 * If you specify an alpha value, you'll get a rgba() value instead.
 *
 * @param The hex value to convert. ('123456'. '#123456', ''123', '#123')
 * @param An alpha value to apply. (optional) ('0.5', '0.25')
 * @return An rgb or rgba value. ('rgb(11, 22, 33)'. 'rgba(11, 22, 33, 0.5)')
 */
export const hexToRgba = (hex, a) => {
	const hashlessHex = removeHash(hex);
	const hexObject = parseHex(hashlessHex);
	const decimalObject = hexesToDecimals(hexObject);

	return formatRgb(decimalObject, a);
};

export const getSystemStyle = (style: SystemStylesTypes['variants']) => {
	return getComputedStyle(document.documentElement).getPropertyValue(style);
};

// HEX TO RGBA CONVERTOR
const removeHash = (hex) => (hex.charAt(0) === '#' ? hex.slice(1) : hex);

const parseHex = (nakedHex) => {
	const isShort = nakedHex.length === 3 || nakedHex.length === 4;

	const twoDigitHexR = isShort
		? `${nakedHex.slice(0, 1)}${nakedHex.slice(0, 1)}`
		: nakedHex.slice(0, 2);
	const twoDigitHexG = isShort
		? `${nakedHex.slice(1, 2)}${nakedHex.slice(1, 2)}`
		: nakedHex.slice(2, 4);
	const twoDigitHexB = isShort
		? `${nakedHex.slice(2, 3)}${nakedHex.slice(2, 3)}`
		: nakedHex.slice(4, 6);
	const twoDigitHexA =
		(isShort ? `${nakedHex.slice(3, 4)}${nakedHex.slice(3, 4)}` : nakedHex.slice(6, 8)) || 'ff';

	return {
		r: twoDigitHexR,
		g: twoDigitHexG,
		b: twoDigitHexB,
		a: twoDigitHexA,
	};
};

const hexToDecimal = (hex) => parseInt(hex, 16);

const hexesToDecimals = ({ r, g, b, a }) => ({
	r: hexToDecimal(r),
	g: hexToDecimal(g),
	b: hexToDecimal(b),
	a: +(hexToDecimal(a) / 255).toFixed(2),
});

const isNumeric = (n) => !isNaN(parseFloat(n)) && isFinite(n);

const formatRgb = (decimalObject, parameterA) => {
	const { r, g, b, a: parsedA } = decimalObject;
	const a = isNumeric(parameterA) ? parameterA : parsedA;

	return `rgba(${r}, ${g}, ${b}, ${a})`;
};
